import React, { useEffect, useState } from "react";
import { hidePopUp } from "@store/ui/uiSlice";
import useAppDispatch from "@hooks/useAppDispatch";
import { useTranslation } from "react-i18next";
import Modal from "@components/base/modal";
import { Frequencies, RecurringIssue } from "@store/recurringIssues/types";
import {
  createRecurringIssue,
  updateRecurringIssue,
} from "@store/recurringIssues/recurringIssueThunk";
import { EntityId } from "@reduxjs/toolkit";
import useAppSelector from "@hooks/useAppSelector";
import { selectAllSpaces } from "@store/space/spaceSelectors";
import { selectCurrentUserId } from "@store/user/userSelectors";
import { IssueType } from "@store/issue/types";
import NewRecurringIssueScreen from "./NewRecurringIssueScreen";
import { selectRecurringIssueById } from "@store/recurringIssues/recurringIssueSelectors";
import { isEqual } from "lodash";
import { startOfTomorrow } from "date-fns";
import { generateSections } from "@store/issue/issueHelpers";

interface NewRecurringIssueFormProps {
  refetch: () => void;
  selectedIssueId?: EntityId;
}

const NewRecurringIssueForm: React.FC<NewRecurringIssueFormProps> = ({
  refetch,
  selectedIssueId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const spaces = useAppSelector(({ space }) => selectAllSpaces(space));
  const currentUserId = useAppSelector(selectCurrentUserId);

  const [issue, setIssue] = useState<Omit<RecurringIssue, "id">>({
    spaceIds: [],
    label: "",
    createdBy: currentUserId?.toString() ?? "",
    reportedBy: "",
    description: "",
    issueType: IssueType.TECHNICAL,
    startDate: startOfTomorrow(),
    endDate: null,
    nextCreationDate: startOfTomorrow(),
    createOnCompletion: false,
    interval: 1,
    frequency: Frequencies.Daily,
    attachment: undefined,
  });

  const [hasChanges, setHasChanges] = useState(!selectedIssueId);

  const selectedIssue = useAppSelector((state) =>
    selectedIssueId
      ? selectRecurringIssueById(state, selectedIssueId)
      : undefined,
  );

  useEffect(() => {
    if (!selectedIssue) return;

    const selectedAttachmentUrl =
      selectedIssue.attachment?.url ?? selectedIssue.attachmentUrl;
    const selectedAttachment = selectedAttachmentUrl?.startsWith("blob:")
      ? undefined
      : selectedAttachmentUrl
        ? {
            id: selectedIssue.hktdAttachmentId ?? selectedIssue.attachment?.id,
            url: selectedAttachmentUrl,
          }
        : selectedIssue.attachment;

    setIssue({
      ...selectedIssue,
      startDate: new Date(selectedIssue.startDate),
      endDate: selectedIssue.endDate ? new Date(selectedIssue.endDate) : null,
      attachment: selectedAttachment,
    });
  }, [selectedIssue]);

  const canSubmit =
    issue.spaceIds !== undefined &&
    issue.spaceIds.length > 0 &&
    issue.label !== undefined &&
    issue.label?.length > 1 &&
    issue.reportedBy !== undefined &&
    issue.reportedBy?.length > 1 &&
    hasChanges;

  const onSubmit = () => {
    if (!selectedIssueId) {
      dispatch(createRecurringIssue({ issue, onSuccess: refetch }));
    } else {
      dispatch(
        updateRecurringIssue({
          id: selectedIssueId,
          issue,
          onSuccess: refetch,
        }),
      );
    }
    dispatch(hidePopUp());
  };

  useEffect(() => {
    if (!selectedIssue) return;
    setHasChanges(!isEqual(selectedIssue, issue));
  }, [selectedIssue, issue]);

  return (
    <Modal
      title={selectedIssueId ? t("tasks.change") : t("tasks.addNew")}
      onClose={() => dispatch(hidePopUp())}
      setShowModal={() => dispatch(hidePopUp())}
      isOpen>
      <NewRecurringIssueScreen
        sections={generateSections(spaces)}
        issue={issue}
        setIssue={setIssue}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        isInEditMode={!!selectedIssueId}
      />
    </Modal>
  );
};

export default NewRecurringIssueForm;
